import type { DialogBaseProps } from "@freightsimple/ui-core/Dialog/DialogBaseProps";
import { Button } from "@freightsimple/ui-core/Controls/Button";
import { ControlPresentationProvider } from "@freightsimple/ui-core/Controls/ControlPresentationProvider";
import { Dialog } from "@freightsimple/ui-core/Dialog/Dialog";
import { Row } from "@freightsimple/ui-core/ComponentLayout/Row";
import type { Shipment } from "@freightsimple/generated-dashboard-openapi-client/models/Shipment";
import { useState } from "react";
import { ModifyShipmentPickupDetailsPage } from "@/Features/ModifyShipment/Pages/ModifyShipmentPickupDetailsPage";
import { ModifyShipmentPickupAddressPage } from "@/Features/ModifyShipment/Pages/ModifyShipmentPickupAddressPage";
import { ModifyShipmentPickupContactPage } from "@/Features/ModifyShipment/Pages/ModifyShipmentPickupContactPage";
import { ModifyShipmentPickupNotesPage } from "@/Features/ModifyShipment/Pages/ModifyShipmentPickupNotesPage";
import { ModifyShipmentDeliveryDetailsPage } from "@/Features/ModifyShipment/Pages/ModifyShipmentDeliveryDetailsPage";
import { ModifyShipmentDeliveryAddressPage } from "@/Features/ModifyShipment/Pages/ModifyShipmentDeliveryAddressPage";
import { ModifyShipmentDeliveryContactPage } from "@/Features/ModifyShipment/Pages/ModifyShipmentDeliveryContactPage";
import { ModifyShipmentDeliveryNotesPage } from "@/Features/ModifyShipment/Pages/ModifyShipmentDeliveryNotesPage";
import { ModifyShipmentLineItemsPage } from "@/Features/ModifyShipment/Pages/ModifyShipmentLineItemsPage";

interface CurrentPageProps {
  currentPage: number;
  shipment: Shipment;
}

function CurrentPage_(props: CurrentPageProps) {
  switch (props.currentPage) {
    case 1:
      return <ModifyShipmentPickupDetailsPage shipment={props.shipment} />;
    case 2:
      return <ModifyShipmentPickupAddressPage shipment={props.shipment} />;
    case 3:
      return <ModifyShipmentPickupContactPage shipment={props.shipment} />;
    case 4:
      return <ModifyShipmentPickupNotesPage shipment={props.shipment} />;
    case 5:
      return <ModifyShipmentDeliveryDetailsPage shipment={props.shipment} />;
    case 6:
      return <ModifyShipmentDeliveryAddressPage shipment={props.shipment} />;
    case 7:
      return <ModifyShipmentDeliveryContactPage shipment={props.shipment} />;
    case 8:
      return <ModifyShipmentDeliveryNotesPage shipment={props.shipment} />;
    case 9:
      return <ModifyShipmentLineItemsPage shipment={props.shipment} />;
    default:
      throw new Error(`Unhandled page ${props.currentPage}`);
  }
}

interface ModifyShipmentDialogProps extends DialogBaseProps {
  shipment: Shipment;
}

// Employee-only (ctrl+m) modify-shipment wizard: nine pages stepped through
// with Previous/Next inside one dialog. Replaces the legacy imperative modal.
export function ModifyShipmentDialog(props: ModifyShipmentDialogProps) {
  const [currentPage, setCurrentPage] = useState(1);

  function onPrevious() {
    if (currentPage > 1) {
      setCurrentPage(currentPage - 1);
    }
  }

  function onNext() {
    if (currentPage <= 9) {
      setCurrentPage(currentPage + 1);
    }
  }

  return (
    <Dialog
      open={props.open}
      onOpenChange={props.onOpenChange}
      title={`Modify Shipment : ${currentPage}`}
      description="If any of the details of your shipment are incorrect, please modify them here"
      width="medium"
      footer={
        <Row justify="between">
          <Button onClick={() => props.onOpenChange?.(false)}>Cancel</Button>
          <Row gap={4}>
            <Button primary onClick={onPrevious}>
              Previous
            </Button>
            <Button primary onClick={onNext}>
              Next
            </Button>
          </Row>
        </Row>
      }
    >
      <ControlPresentationProvider presentation="underlined">
        <CurrentPage_ currentPage={currentPage} shipment={props.shipment} />
      </ControlPresentationProvider>
    </Dialog>
  );
}
